import { Map } from "immutable";
import * as React from 'react';
import ResizeObserverPolyfill from '@juggle/resize-observer';

import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import rafSchedule from 'raf-schd';

import {
    NoteColor,
    NOTE_SIZE,
    ProcessStep,
} from '../constants';
import { DraggingState, Note, TemplateData } from '../reducers';

import NoteDefs from './NoteDefs';
import NoteView from './NoteView';
import TemplateView from './TemplateView';
import { PanningState } from "../reducers/ui-state";


interface Props {
    readonly processStep: ProcessStep,
    readonly width: number,
    readonly height: number,
    readonly notes: ReadonlyArray<Note>,
    readonly zoomLevel: number,
    readonly scrollLeft: number,
    readonly scrollTop: number,
    readonly panning: PanningState | null,
    readonly dragging: DraggingState | null,
    readonly templateData: TemplateData,
    readonly textFields: Map<string, string>,
    readonly templateTextSupplier: (id: string) => string,
    readonly onStartPanning: (mouseX: number, mouseY: number) => void,
    readonly onPanning: (mouseX: number, mouseY: number) => void,
    readonly onStopPanning: (mouseX: number, mouseY: number) => void,
    readonly onNoteStartDragging: (uuid: string, x: number, y: number) => void,
    readonly onNoteDragging: (x: number, y: number) => void,
    readonly onNoteStopDragging: (x: number, y: number) => void,
    readonly onNoteDoubleClicked: (uuid: string, text: string, color: NoteColor) => void,
    readonly onDeleteButtonClicked: (uuid: string) => void,
    readonly onBackgroundDoubleClicked: () => void,
    readonly onTemplateHelpClicked: (content: string) => void,
    readonly onTextFieldClicked: (key: string, text: string) => void,
    readonly onScrolled: (scrollLeft: number, scrollRight: number) => void,
    readonly onCanvasResized: (width: number, height: number) => void,
}


const styles = (theme: Theme) => createStyles({
    root: {
        backgroundColor: theme.palette.grey[700],
        width: '100%',
        height: '100%',
        overflow: 'auto',
    },
    rootNoDrag: {
        cursor: 'default',
    },
    rootDrag: {
        cursor: 'grabbing',
    },
});

type Styles = 'root' | 'rootNoDrag' | 'rootDrag';


class Canvas extends React.Component<Props & WithStyles<Styles>> {

    private svg: any;
    private background: HTMLElement | null;
    private resizeObserver: any;
    private readonly scheduleMouseMove: ((clientX: number, clientY: number) => void) & { cancel: () => void };
    private readonly scheduleScroll: (() => void) & { cancel: () => void };

    constructor(props: Props & WithStyles<Styles>) {
        super(props);
        this.scheduleMouseMove = rafSchedule(
            (clientX: number, clientY: number) => {
                const { dragging, panning } = this.props;
                if (dragging) {
                    const { width, templateData, onNoteDragging, onNoteStopDragging } = this.props;
                    const height = templateData.canvasHeight;
                    const localPoint = this.calculateLocalPoint(clientX, clientY);
                    const x = localPoint.x + dragging.offsetX;
                    const y = localPoint.y + dragging.offsetY;
                    if (x < -NOTE_SIZE / 4 || x > width - NOTE_SIZE / 2 || y < -NOTE_SIZE / 4 || y > height - NOTE_SIZE / 2) {
                        onNoteStopDragging(
                            Math.max(-NOTE_SIZE / 4, Math.min(x, width - NOTE_SIZE / 2)),
                            Math.max(-NOTE_SIZE / 4, Math.min(y, height - NOTE_SIZE / 2))
                        );
                    } else {
                        onNoteDragging(x, y);
                    }
                } else if (panning) {
                    const { width, height, onPanning, onStopPanning } = this.props;
                    if (clientX <= 0 || clientX >= width || clientY <= 0 || clientY >= height) {
                        onStopPanning(Math.max(0, Math.min(clientX, width)), Math.max(0, Math.min(clientY, height)));
                    } else {
                        onPanning(clientX, clientY);
                    }
                }
            }
        );
        this.scheduleScroll = rafSchedule(
            () => {
                const background = this.background;
                if (background) {
                    this.props.onScrolled(background.scrollLeft, background.scrollTop);
                }
            }
        );

        // @ts-ignore
        const ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill;
        this.resizeObserver = new ResizeObserver(() => {
            const background = this.background;
            if (background) {
                const rect = background.getBoundingClientRect();
                this.props.onCanvasResized(rect.width, rect.height);
            }
        });
    }

    public componentDidMount() {
        const background = this.background;
        if (background) {
            const rect = background.getBoundingClientRect();
            this.props.onCanvasResized(rect.width, rect.height);
            this.resizeObserver.observe(background);
        }
    }

    public componentWillUnmount() {
        this.scheduleMouseMove.cancel();
        this.scheduleScroll.cancel();
        this.resizeObserver.disconnect();
    }

    public componentWillUpdate(nextProps: Props) {
        const background = this.background;

        const oldScrollLeft = this.props.scrollLeft;
        const newScrollLeft = nextProps.scrollLeft;
        if (background && oldScrollLeft !== newScrollLeft) {
            background.scrollLeft = newScrollLeft;
        }

        const oldScrollTop = this.props.scrollTop;
        const newScrollTop = nextProps.scrollTop;
        if (background && oldScrollTop !== newScrollTop) {
            background.scrollTop = newScrollTop;
        }
    }

    public render() {
        return (
            <div
                className={this.props.classes.root}
                ref={element => this.background = element}
                onScroll={this.handleScroll}
            >
                <svg
                    ref={(element) => this.svg = element}
                    className={this.props.dragging || this.props.panning? this.props.classes.rootDrag : this.props.classes.rootNoDrag}
                    width={`${this.props.zoomLevel * 100}%`}
                    height={`${this.props.zoomLevel * 100}%`}
                    viewBox={`0 0 ${this.props.width} ${this.props.templateData.canvasHeight}`}
                    onMouseDown={this.handleMouseDown}
                    onMouseMove={this.handleMouseMove}
                    onMouseUp={this.handleMouseUp}
                    onDoubleClick={this.props.onBackgroundDoubleClicked}
                >

                    <NoteDefs/>

                    <rect fill="white" width={this.props.width} height={this.props.templateData.canvasHeight}/>

                    {this.props.processStep === ProcessStep.STRUCTURE &&
                        <TemplateView
                            templateData={this.props.templateData}
                            textFields={this.props.textFields}
                            templateTextSupplier={this.props.templateTextSupplier}
                            onHelpClicked={this.props.onTemplateHelpClicked}
                            onTextFieldClicked={this.props.onTextFieldClicked}
                        />
                    }

                    {this.props.notes.map((note, index) =>
                        <NoteView
                            key={index}
                            {...note}
                            dragging={this.props.dragging ? this.props.dragging.uuid === note.uuid : false}
                            onMouseDown={(e: React.MouseEvent<SVGElement>) => this.handleNoteMouseDown(note.uuid, e)}
                            onDoubleClicked={() => this.props.onNoteDoubleClicked(note.uuid, note.text, note.color)}
                            onDeleteButtonClicked={() => this.props.onDeleteButtonClicked(note.uuid)}
                        />
                    )}
                </svg>
            </div>
        );
    }

    private readonly handleScroll = () => {
        this.scheduleScroll();
    };

    private calculateLocalPoint(x: number, y: number) {
        if (this.svg) {
            const point = this.svg.createSVGPoint();
            point.x = x;
            point.y = y;
            return point.matrixTransform(this.svg.getScreenCTM().inverse());
        }
        return { x: 0, y: 0 };
    }

    private handleNoteMouseDown(uuid: string, e: React.MouseEvent<SVGElement>) {
        const { onNoteStartDragging } = this.props;
        const localPoint = this.calculateLocalPoint(e.clientX, e.clientY);
        onNoteStartDragging(uuid, localPoint.x, localPoint.y);
    }

    private readonly handleMouseDown = (e: React.MouseEvent<SVGElement>) => {
        this.props.onStartPanning(e.clientX, e.clientY);
    };

    private readonly handleMouseMove = (e: React.MouseEvent<SVGElement>) => {
        this.scheduleMouseMove(e.clientX, e.clientY);
    };

    private readonly handleMouseUp = (e: React.MouseEvent<SVGElement>) => {
        const { width, height, dragging, panning, onNoteStopDragging, onStopPanning } = this.props;
        if (dragging) {
            const localPoint = this.calculateLocalPoint(e.clientX, e.clientY);
            const x = localPoint.x + dragging.offsetX;
            const y = localPoint.y + dragging.offsetY;
            onNoteStopDragging(
                Math.max(-NOTE_SIZE / 4, Math.min(x, width - NOTE_SIZE / 2)),
                Math.max(-NOTE_SIZE / 4, Math.min(y, height - NOTE_SIZE / 2))
            );
        } else if (panning) {
            onStopPanning(
                Math.max(0, Math.min(e.clientX, width)),
                Math.max(0, Math.min(e.clientY, height))
            );
        }
    };
}

export default withStyles(styles)(Canvas);



// WEBPACK FOOTER //
// ./src/components/CanvasView.tsx